import { Controller } from "@hotwired/stimulus"

// Sub Directory (Buyout tab) — SubVendor CRUD via fetch with DOM-in-place
// row swaps (no reloads), client-side search + trade filtering, clickable
// coverage chips, and a comma/Enter trades chip input in the add/edit modal.
export default class extends Controller {
  static targets = [
    "tableWrap", "tbody", "emptyState", "noMatches", "vendorRow",
    "coverageWrap", "filterBar", "searchInput", "tradeSelect", "clearFiltersBtn",
    "vendorModal", "vendorModalTitle", "vendorModalError", "vendorSaveBtn",
    "fieldName", "fieldContactName", "fieldEmail", "fieldPhone",
    "fieldLicense", "fieldInsurance", "fieldW9", "fieldNotes",
    "tradeChips", "tradeInput",
    "deleteModal", "deleteName"
  ]

  static values = {
    createUrl: String,
    vendorUrlTemplate: String // ".../buyout_subs/0" — 0 swapped for the id
  }

  connect() {
    this._editingId = null
    this._pendingDeleteId = null
    this._trades = []
  }

  // ── Search + trade filter ───────────────────────────────────────────

  applyFilters() {
    const query = this.searchInputTarget.value.trim().toLowerCase()
    const trade = this.tradeSelectTarget.value // already lowercase
    let visible = 0

    this.vendorRowTargets.forEach(row => {
      const matchesQuery = !query || (row.dataset.vendorSearch || "").includes(query)
      const rowTrades = (row.dataset.vendorTradesLower || "").split("|").filter(Boolean)
      const matchesTrade = !trade || rowTrades.includes(trade)
      const show = matchesQuery && matchesTrade
      row.classList.toggle("hidden", !show)
      if (show) visible++
    })

    this.noMatchesTarget.classList.toggle("hidden", visible > 0 || this.vendorRowTargets.length === 0)
    this.clearFiltersBtnTarget.classList.toggle("hidden", !query && !trade)
  }

  filterByCoverageChip(event) {
    event.preventDefault()
    const trade = (event.currentTarget.dataset.trade || "").toLowerCase()
    // Toggle: clicking the active chip clears the filter.
    this.tradeSelectTarget.value = this.tradeSelectTarget.value === trade ? "" : trade
    this.applyFilters()
  }

  clearFilters(event) {
    event?.preventDefault()
    this.searchInputTarget.value = ""
    this.tradeSelectTarget.value = ""
    this.applyFilters()
  }

  // ── Add/Edit modal ──────────────────────────────────────────────────

  showNewModal(event) {
    event?.preventDefault()
    this._editingId = null
    this.vendorModalTitleTarget.textContent = "Add Sub"
    this._fillForm({})
    this._clearError()
    this.vendorModalTarget.classList.remove("hidden")
    this.fieldNameTarget.focus()
  }

  showEditModal(event) {
    event.preventDefault()
    const d = event.currentTarget.closest("tr").dataset
    this._editingId = d.vendorId
    this.vendorModalTitleTarget.textContent = "Edit Sub"
    this._fillForm({
      name: d.vendorName, contactName: d.vendorContactName, email: d.vendorEmail,
      phone: d.vendorPhone, trades: d.vendorTrades, license: d.vendorLicense,
      insurance: d.vendorInsurance, w9: d.vendorW9 === "true", notes: d.vendorNotes
    })
    this._clearError()
    this.vendorModalTarget.classList.remove("hidden")
    this.fieldNameTarget.focus()
  }

  hideVendorModal(event) {
    event?.preventDefault()
    this.vendorModalTarget.classList.add("hidden")
  }

  async saveVendor(event) {
    event.preventDefault()
    this.commitTradeInput()
    const name = this.fieldNameTarget.value.trim()
    if (!name) {
      this._setError("Give the sub a company name.")
      return
    }

    const payload = {
      sub_vendor: {
        name,
        contact_name: this.fieldContactNameTarget.value.trim(),
        email: this.fieldEmailTarget.value.trim(),
        phone: this.fieldPhoneTarget.value.trim(),
        trades: this._trades.join(", "),
        license_number: this.fieldLicenseTarget.value.trim(),
        insurance_expires_on: this.fieldInsuranceTarget.value.trim(),
        w9_on_file: this.fieldW9Target.checked,
        notes: this.fieldNotesTarget.value
      }
    }

    const isEdit = !!this._editingId
    const url = isEdit ? this._vendorUrl(this._editingId) : this.createUrlValue
    this.vendorSaveBtnTarget.disabled = true

    try {
      const res = await fetch(url, {
        method: isEdit ? "PATCH" : "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify(payload)
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Something went wrong")

      if (isEdit) {
        const row = document.getElementById(`sub-vendor-row-${this._editingId}`)
        if (row) row.outerHTML = data.row
        this._showToast("Sub updated")
      } else {
        this.tbodyTarget.insertAdjacentHTML("afterbegin", data.row)
        this.tableWrapTarget.classList.remove("hidden")
        this.filterBarTarget.classList.remove("hidden")
        this.emptyStateTarget.classList.add("hidden")
        this._showToast("Sub added to your directory")
      }
      this._applyCoverage(data)
      this.hideVendorModal()
      this.applyFilters()
    } catch (e) {
      this._setError(e.message)
    } finally {
      this.vendorSaveBtnTarget.disabled = false
    }
  }

  // ── Trades chip input (comma / Enter adds a chip) ───────────────────

  focusTradeInput(event) {
    if (event.target === event.currentTarget) this.tradeInputTarget.focus()
  }

  tradeInputKeydown(event) {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault()
      this.commitTradeInput()
    } else if (event.key === "Backspace" && !this.tradeInputTarget.value) {
      this._trades.pop()
      this._renderTradeChips()
    }
  }

  commitTradeInput() {
    const raw = this.tradeInputTarget.value
    raw.split(",").map(t => t.trim()).filter(Boolean).forEach(trade => {
      if (!this._trades.some(t => t.toLowerCase() === trade.toLowerCase())) this._trades.push(trade)
    })
    this.tradeInputTarget.value = ""
    this._renderTradeChips()
  }

  removeTradeChip(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.index, 10)
    this._trades.splice(index, 1)
    this._renderTradeChips()
  }

  _renderTradeChips() {
    this.tradeChipsTarget.innerHTML = ""
    this._trades.forEach((trade, index) => {
      const chip = document.createElement("span")
      chip.className = "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[13px] font-medium bg-blue-50 text-blue-700"
      const label = document.createElement("span")
      label.textContent = trade
      chip.appendChild(label)
      const btn = document.createElement("button")
      btn.type = "button"
      btn.dataset.index = index
      btn.setAttribute("data-action", "click->sub-vendors#removeTradeChip")
      btn.className = "text-blue-400 hover:text-blue-700 cursor-pointer leading-none"
      btn.textContent = "×"
      chip.appendChild(btn)
      this.tradeChipsTarget.appendChild(chip)
    })
  }

  // ── Delete (styled confirm modal — soft delete) ─────────────────────

  showDeleteModal(event) {
    event.preventDefault()
    const d = event.currentTarget.closest("tr").dataset
    this._pendingDeleteId = d.vendorId
    this.deleteNameTarget.textContent = d.vendorName || "This sub"
    this.deleteModalTarget.classList.remove("hidden")
  }

  hideDeleteModal(event) {
    event?.preventDefault()
    this.deleteModalTarget.classList.add("hidden")
    this._pendingDeleteId = null
  }

  async confirmDelete(event) {
    event.preventDefault()
    const id = this._pendingDeleteId
    if (!id) return

    try {
      const res = await fetch(this._vendorUrl(id), {
        method: "DELETE",
        headers: this._jsonHeaders()
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Failed to remove the sub")

      document.getElementById(`sub-vendor-row-${id}`)?.remove()
      if (this.vendorRowTargets.length === 0) {
        this.tableWrapTarget.classList.add("hidden")
        this.filterBarTarget.classList.add("hidden")
        this.emptyStateTarget.classList.remove("hidden")
      }
      this._applyCoverage(data)
      this.applyFilters()
      this._showToast("Sub removed from your directory")
    } catch (e) {
      this._showToast(e.message, "error")
    } finally {
      this.hideDeleteModal()
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  _fillForm({ name, contactName, email, phone, trades, license, insurance, w9, notes }) {
    this.fieldNameTarget.value = name || ""
    this.fieldContactNameTarget.value = contactName || ""
    this.fieldEmailTarget.value = email || ""
    this.fieldPhoneTarget.value = phone || ""
    this.fieldLicenseTarget.value = license || ""
    this._setDate(insurance || "")
    this.fieldW9Target.checked = !!w9
    this.fieldNotesTarget.value = notes || ""
    this._trades = (trades || "").split(",").map(t => t.trim()).filter(Boolean)
    this.tradeInputTarget.value = ""
    this._renderTradeChips()
  }

  // Server returns the refreshed coverage strip + trades-in-use after every
  // mutation; swap the strip and rebuild the filter dropdown options.
  _applyCoverage(data) {
    if (data.coverage !== undefined && this.hasCoverageWrapTarget) {
      this.coverageWrapTarget.innerHTML = data.coverage
      this.coverageWrapTarget.classList.toggle("hidden", !data.coverage.trim())
    }
    if (Array.isArray(data.trades) && this.hasTradeSelectTarget) {
      const current = this.tradeSelectTarget.value
      this.tradeSelectTarget.innerHTML = ""
      const all = document.createElement("option")
      all.value = ""
      all.textContent = "All trades"
      this.tradeSelectTarget.appendChild(all)
      data.trades.forEach(trade => {
        const opt = document.createElement("option")
        opt.value = trade.toLowerCase()
        opt.textContent = trade
        this.tradeSelectTarget.appendChild(opt)
      })
      if ([...this.tradeSelectTarget.options].some(o => o.value === current)) {
        this.tradeSelectTarget.value = current
      }
    }
  }

  _vendorUrl(id) {
    return this.vendorUrlTemplateValue.replace(/\/0$/, `/${id}`)
  }

  _setDate(value) {
    const input = this.fieldInsuranceTarget
    input.value = value || ""
    if (input._flatpickr) input._flatpickr.setDate(value || null, false)
  }

  _setError(message) {
    this.vendorModalErrorTarget.textContent = message
    this.vendorModalErrorTarget.classList.remove("hidden")
  }

  _clearError() {
    this.vendorModalErrorTarget.textContent = ""
    this.vendorModalErrorTarget.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  _jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
